import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useEndpoint from '../../../hooks/useEndpoint';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import EndpointOverview from './EndpointOverview';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import useAnalytics from '../../../hooks/useAnalytics';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import {
  MethodsTitle,
  Row,
  MethodLabel,
  FunctionBehaviorChartContainer,
} from './EndpointPage.styles';
import { Row as RowBase } from '@hud/web-components/Layout/Layout';
import Page from '../../../components/Page/Page';
import EndpointBehavior from './EndpointBehavior';
import { MethodsPopover } from '../../../components/MethodsPopover/MethodsPopover';
import { Endpoint } from '@hud/portal-dtos/webapp-endpoint.dto';
import FunctionsTable from '../../../components/FunctionsTable/FunctionsTable';
import { GetFunctionsQuery } from '@hud/portal-dtos/webapp-function.dto';
import { useDebouncedValue } from '@mantine/hooks';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import useFlowFunctions from '../../../hooks/useFlowFunctions';
import EndpointStatusCodesTable from './EndpointStatusCodesTable';
import { useFunctionFilters } from '../../../hooks/useFunctionFilter';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import useEndpoints from '../../../hooks/useEndpoints';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { TagRecordSchema, ThresholdFilter } from '@hud/portal-dtos/webapp-common.dto';
import {
  CommonThresholdQueryParams,
  getThresholdFilter,
  ThresholdFilterKey,
  getMethodsDisplayText,
} from '../../../utils';
import { Events } from '../../../analytics';
import IssuesTable from '../../HeadsUp/HeadsUpIssuesPage/IssuesTable';
import useIssues from '../../../hooks/useIssues';
import { SortingState } from '@tanstack/react-table';
import useRulesNames from '../../../hooks/useRulesNames';

function getEndpointTitle(endpoint: Endpoint) {
  if (endpoint.methods.length === 1) {
    const method = endpoint.methods[0];
    return (
      <MethodsTitle>
        <MethodLabel key={method}>{method.toLocaleUpperCase()}</MethodLabel>
      </MethodsTitle>
    );
  }

  return (
    <MethodsTitle>
      <MethodsPopover methods={endpoint.methods} />
    </MethodsTitle>
  );
}

function useFallbackEndpoint(endpointId: number, serviceId: number) {
  const navigate = useNavigate();
  const globalFilters = useGlobalQueryString();

  const endpointsQuery = useEndpoints(
    { endpointIds: [endpointId] },
    Boolean(endpointId && !serviceId),
  );

  useEffect(() => {
    if (endpointId && serviceId) {
      return;
    }
    const data = endpointsQuery.data;
    if (!data) {
      return;
    }
    if (data.countFiltered === 0) {
      console.log('No fallback endpoint found');
      return;
    }
    const endpoint = data.endpoints[0];
    if (data.countFiltered === 1) {
      // if only 1 redirect to endpoint/endpointId-serviceId
      navigate(
        `/explore/endpoints/${endpoint.id}-${endpoint.service.id}?${globalFilters}`,
        { replace: true },
      );
    } else {
      // redirect to endpoints/?search=endpoint.path
      navigate(`/explore/endpoints/?${globalFilters}&search=${endpoint.path}`, {
        replace: true,
      });
    }
  }, [endpointsQuery.data]);
}

type EndpointPageQueryParams = CommonTableQueryParams &
  CommonThresholdQueryParams & {
    files: string[];
    services: number[];
    flowDeployments: string[];
    showUnnamed: boolean;
    tags: string[];
    // Issues table state
    issuesSearch: string;
    issuesPage: number;
    issuesOrderBy: string;
    issuesDesc: boolean;
    issuesFilteredServices: number[];
    issuesFilteredRules: string[];
  };

export default function EndpointPage() {
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const { endpointAndService } = useParams<{ endpointAndService: string }>();
  const [endpointId, serviceId] = (endpointAndService || '').split('-');
  const analytics = useAnalytics();
  const [initialLoad, setInitialLoad] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const functionFilters = useFunctionFilters();
  const globalUrlParams = useGlobalQueryString();

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<EndpointPageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'durationAvg.value',
      desc: true,

      files: [],
      services: [],
      flowDeployments: [],
      tags: [],
      showUnnamed: false,
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,

      // Issues table state
      issuesSearch: '',
      issuesPage: 1,
      issuesOrderBy: 'detectionTime',
      issuesDesc: true,
      issuesFilteredServices: [],
      issuesFilteredRules: [],
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  const back = Number.parseInt(searchParams.get('b') ?? '1');
  const baseBreadCrumbs = [{ title: 'Endpoints', to: '/explore/endpoints', back }];

  const parsedTags = TagRecordSchema.safeParse(pageState.tags);
  let tags: Record<string, string> | undefined;
  if (parsedTags.success) {
    tags = parsedTags.data;
  } else {
    tags = undefined;
    console.error(parsedTags.error.format());
  }

  const endpointQuery = useEndpoint(
    parseInt(endpointId as string),
    parseInt(serviceId as string) || undefined,
    {
      timeFrame,
      environmentName: environment?.name,
      tags,
    },
  );
  useFallbackEndpoint(parseInt(endpointId as string), parseInt(serviceId as string));

  const [functionsPages, setFunctionsPages] = useState<number>(0);

  const clearAllFilters = () => {
    setPageState({
      files: [],
      services: [],
      flowDeployments: [],
      tags: [],
      showUnnamed: false,
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
      // Clear issues filters too
      issuesSearch: '',
      issuesPage: 1,
      issuesFilteredServices: [],
      issuesFilteredRules: [],
    });
  };

  const handleFilteredFilePathsChange = useCallback(
    (value: string[]) => {
      setPageState({ files: value, page: 1 });
    },
    [setPageState],
  );

  const handleFilteredServicesChange = useCallback(
    (value: number[]) => {
      setPageState({ services: value, page: 1 });
    },
    [setPageState],
  );

  const handleFilteredFlowDeploymentsChange = useCallback(
    (value: string[]) => {
      setPageState({ flowDeployments: value, page: 1 });
    },
    [setPageState],
  );

  const handleShowUnnamedChange = useCallback(
    (value: boolean) => {
      setPageState({ showUnnamed: value, page: 1 });
    },
    [setPageState],
  );

  const handleThresholdFilterChange = (
    key: ThresholdFilterKey,
    value: ThresholdFilter | undefined,
    eventName: keyof Events,
  ) => {
    setPageState({
      [key]: value?.value,
      [`${key}_op`]: value?.operator,
      page: 1,
    });

    analytics.capture(eventName, {
      environment: environment?.name,
      toggle_value: !!value,
      filter_value: value?.value ?? 0,
      filter_operator: value?.operator ?? 'gte',
      endpoint_id: Number.parseInt(endpointId as string),
    });
  };

  const handleFilteredDurationAvgChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'durationAvg',
      value,
      'endpoint_functions_duration_avg_filter_toggled',
    );
  };

  const handleFilteredNormalizedRunsChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'normalizedRuns',
      value,
      'endpoint_functions_normalized_runs_filter_toggled',
    );
  };

  const handleFilteredErrorRateChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'errorRate',
      value,
      'endpoint_functions_error_rate_filter_toggled',
    );
  };

  // Issues table handlers
  const handleIssuesSearchChange = useCallback(
    (value: string) => {
      setPageState({ issuesSearch: value, issuesPage: 1 });
    },
    [setPageState],
  );

  const handleIssuesPageChange = useCallback(
    (value: number) => {
      setPageState({ issuesPage: value });
    },
    [setPageState],
  );

  const handleIssuesFilteredServicesChange = useCallback(
    (value: number[]) => {
      setPageState({ issuesFilteredServices: value, issuesPage: 1 });
    },
    [setPageState],
  );

  const handleIssuesFilteredRulesChange = useCallback(
    (value: string[]) => {
      setPageState({ issuesFilteredRules: value, issuesPage: 1 });
    },
    [setPageState],
  );

  const functionsQueryParams = useMemo<GetFunctionsQuery>(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetFunctionsQuery['orderBy'],
      includeGhost: true,
      includeUnnamed: pageState.showUnnamed,
      timeFrame,
      filePaths: pageState.files,
      environmentName: environment?.name,
      flowDeployments: pageState.flowDeployments ?? undefined,
      tags,
      durationAvg: getThresholdFilter(pageState.durationAvg, pageState.durationAvg_op),
      normalizedRuns: getThresholdFilter(
        pageState.normalizedRuns,
        pageState.normalizedRuns_op,
      ),
      errorRate: getThresholdFilter(pageState.errorRate, pageState.errorRate_op),
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      pageState.showUnnamed,
      timeFrame,
      pageState.files,
      environment?.name,
      pageState.durationAvg,
      pageState.durationAvg_op,
      pageState.normalizedRuns,
      pageState.normalizedRuns_op,
      pageState.errorRate,
      pageState.errorRate_op,
      pageState.tags,
      pageState.flowDeployments,
    ],
  );
  const [debouncedFunctionsQueryParams] = useDebouncedValue(
    functionsQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );
  const functionsQuery = useFlowFunctions(
    parseInt(endpointId as string),
    parseInt(serviceId as string),
    debouncedFunctionsQueryParams,
  );

  const issuesQuery = useIssues({
    timeFrame,
    environmentName: environment?.name,
    endpointId: parseInt(endpointId as string),
    serviceId: parseInt(serviceId as string),
  });

  // Fetch rules names for the IssuesTable
  const rulesNamesQuery = useRulesNames();

  // Create sorting state for issues table from pageState
  const issuesSortingState: SortingState = useMemo(() => {
    return [
      {
        id: pageState.issuesOrderBy,
        desc: pageState.issuesDesc,
      },
    ];
  }, [pageState.issuesOrderBy, pageState.issuesDesc]);

  const handleIssuesSortingChange = useCallback(
    (updater: ((old: SortingState) => SortingState) | SortingState) => {
      const newSorting =
        typeof updater === 'function' ? updater(issuesSortingState) : updater;
      if (newSorting.length > 0) {
        const sort = newSorting[0];
        setPageState({
          issuesOrderBy: sort.id,
          issuesDesc: sort.desc,
          issuesPage: 1,
        });
      }
    },
    [setPageState, issuesSortingState],
  );

  useEffect(() => {
    if (functionsQuery.isSuccess) {
      setFunctionsPages(Math.ceil(functionsQuery.data.countFiltered / DEFAULT_PAGE_SIZE));
    }
  }, [functionsQuery.isSuccess, functionsQuery.data]);

  useEffect(() => {
    if (endpointQuery.isSuccess) {
      const methods = getMethodsDisplayText(endpointQuery.data.endpoint.methods);
      document.title = `${methods} • ${endpointQuery.data.endpoint.path} • Hud`;
    } else {
      document.title = 'Hud';
    }
  }, [
    endpointQuery.isSuccess,
    endpointQuery.data?.endpoint.methods,
    endpointQuery.data?.endpoint.path,
  ]);

  useEffect(() => {
    if (endpointQuery.isSuccess) {
      if (!environment?.name) return;
      analytics.capture('endpoint_viewed', {
        endpoint_id: endpointQuery.data.endpoint.id,
        endpoint_path: endpointQuery.data.endpoint.path ?? '',
        environment: environment?.name,
      });
      if (!initialLoad) {
        setInitialLoad(true);
      }
    }
  }, [analytics, environment?.name, initialLoad, endpointQuery]);

  const referralBreadcrumbs = useMemo(() => {
    if (!endpointQuery.data) return;
    return [
      {
        title: 'Endpoint functions',
        to: `/explore/endpoints/${endpointId}-${serviceId}?${functionFilters}`,
        nowrap: true,
      },
    ] as BreadcrumbItem[];
  }, [functionFilters, endpointQuery.data, endpointId]);

  if (endpointQuery.isError) {
    return (
      <Page
        header={baseBreadCrumbs}
        actions={
          <ActionsContainer>
            <EnvironmentMenu />
            <TimeFrameMenu />
            <ThemeButton />
          </ActionsContainer>
        }
      >
        {endpointQuery.error.response?.status === 404
          ? endpointQuery.error.response?.data.message
          : 'Something went wrong'}
      </Page>
    );
  }
  return (
    <Page
      header={
        endpointQuery.isLoading
          ? 'loading'
          : [
              ...baseBreadCrumbs,
              ...(endpointQuery.isSuccess
                ? [
                    {
                      title: getEndpointTitle(endpointQuery.data.endpoint),
                    },
                    {
                      // TODO: handle long paths with reverse ellipsis
                      title: endpointQuery.data.endpoint.path,
                    },
                  ]
                : []),
            ]
      }
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <Row>
        <EndpointOverview
          endpoint={endpointQuery.data?.endpoint}
          loading={endpointQuery.isLoading}
          entryFunction={endpointQuery.data?.endpoint.entryFunction}
          globalUrlParams={globalUrlParams}
          tags={tags}
        />
      </Row>
      <RowBase>
        <FunctionBehaviorChartContainer
          title="Behavior"
          variant="filled"
          icon="webviewMenu.flow"
        >
          <EndpointBehavior
            endpoint={endpointQuery.data?.endpoint}
            graph={endpointQuery.data?.graphs}
            deployments={endpointQuery.data?.deployments}
            issues={issuesQuery.data?.issues.filter(
              (issue) => issue.exemplars.length > 0,
            )}
            loading={endpointQuery.isLoading}
            timeFrame={timeFrame}
            firstSeenAt={endpointQuery.data?.endpoint.firstSeenAt}
          />
        </FunctionBehaviorChartContainer>
      </RowBase>
      <IssuesTable
        issues={issuesQuery.data?.issues ?? []}
        rulesNames={rulesNamesQuery.data?.rules ?? []}
        loading={issuesQuery.isLoading || rulesNamesQuery.isLoading}
        currentPage={pageState.issuesPage}
        onPageChange={handleIssuesPageChange}
        searchValue={pageState.issuesSearch}
        onSearchChange={handleIssuesSearchChange}
        sortingState={issuesSortingState}
        onSortingChange={handleIssuesSortingChange}
        filteredServices={pageState.issuesFilteredServices}
        onFilteredServicesChange={handleIssuesFilteredServicesChange}
        filteredRules={pageState.issuesFilteredRules}
        onFilteredRulesChange={handleIssuesFilteredRulesChange}
        filterType="endpoint"
      />
      <EndpointStatusCodesTable
        statusCodes={endpointQuery.data?.statusCodes}
        endpoint={endpointQuery.data?.endpoint}
        loading={endpointQuery.isLoading}
      />
      <FunctionsTable
        timeFrame={timeFrame}
        title={'Functions in this flow'}
        aggregatedEndpoint={Number.parseInt(endpointId as string)}
        functions={functionsQuery.data?.functions ?? []}
        loading={functionsQuery.isLoading}
        total={functionsQuery.data?.total}
        filtered={functionsQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={functionsPages}
        currentPage={functionsPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        showUnnamed={pageState.showUnnamed}
        onShowUnnamedChange={handleShowUnnamedChange}
        filteredFilePaths={pageState.files}
        onFilteredFilePathsChange={handleFilteredFilePathsChange}
        filteredServices={pageState.services}
        onFilteredServicesChange={handleFilteredServicesChange}
        flowDeploymentsInTimeframe={endpointQuery.data?.deployments}
        filteredFlowDeployments={pageState.flowDeployments}
        onFilteredFlowDeploymentsChange={handleFilteredFlowDeploymentsChange}
        referralBreadcrumbs={referralBreadcrumbs}
        tags={tags}
        filteredDurationAvg={getThresholdFilter(
          pageState.durationAvg,
          pageState.durationAvg_op,
        )}
        onFilteredDurationAvgChange={handleFilteredDurationAvgChange}
        filteredNormalizedRuns={getThresholdFilter(
          pageState.normalizedRuns,
          pageState.normalizedRuns_op,
        )}
        onFilteredNormalizedRunsChange={handleFilteredNormalizedRunsChange}
        filteredErrorRate={getThresholdFilter(
          pageState.errorRate,
          pageState.errorRate_op,
        )}
        onFilteredErrorRateChange={handleFilteredErrorRateChange}
        onClearAllFilters={clearAllFilters}
      />
    </Page>
  );
}
